import { initializeApp } from 'firebase/app';
import {
  initializeFirestore,
  persistentLocalCache,
  persistentMultipleTabManager,
  connectFirestoreEmulator,
} from 'firebase/firestore';
import { getAuth, connectAuthEmulator } from 'firebase/auth';
import { getStorage } from 'firebase/storage';
import { getMessaging, isSupported } from 'firebase/messaging';
import { getFunctions, connectFunctionsEmulator } from 'firebase/functions';

// Get environment from environment variables
// One Firebase project, one Firestore database. `pickupninjaapp` is
// production-only; a staging environment, when it exists, will be a separate
// project rather than a named database in this one — a named database cannot
// isolate Auth, Storage, FCM or quotas, and Gen-1 Firestore triggers only ever
// bind to `(default)`.
const appEnv = process.env.REACT_APP_ENV || 'production';

// Use the same Firebase config as the mobile app
const firebaseConfig = {
  apiKey: process.env.REACT_APP_FIREBASE_API_KEY || "AIzaSyDW_CpzSWNkPhYFMMR9VMpTNd_SKGvRUp8",
  authDomain: process.env.REACT_APP_FIREBASE_AUTH_DOMAIN || "pickupninjaapp.firebaseapp.com",
  databaseURL: "https://pickupninjaapp-default-rtdb.firebaseio.com",
  projectId: process.env.REACT_APP_FIREBASE_PROJECT_ID || "pickupninjaapp",
  storageBucket: process.env.REACT_APP_FIREBASE_STORAGE_BUCKET || "pickupninjaapp.appspot.com",
  messagingSenderId: process.env.REACT_APP_FIREBASE_MESSAGING_SENDER_ID || "415654548533",
  appId: process.env.REACT_APP_FIREBASE_APP_ID || "1:415654548533:web:3e82708730358c00cb0d7c",
  measurementId: "G-2VH5Q85TQK"
};

console.log(`🔥 Firebase initialized for ${appEnv} environment`);

// Initialize Firebase
const app = initializeApp(firebaseConfig);

// Initialize Firestore with IndexedDB-backed offline persistence so the admin
// panel stays usable (read-only) during brief network interruptions.
// persistentMultipleTabManager allows multiple browser tabs to share one cache.
export const db = initializeFirestore(
  app,
  {
    localCache: persistentLocalCache({
      tabManager: persistentMultipleTabManager(),
    }),
  }
);

// Export environment info
export const environment = appEnv;
export const isProduction = appEnv === 'production';
export const isStaging = appEnv === 'staging';
export const auth = getAuth(app);
export const storage = getStorage(app);
export const functions = getFunctions(app);

// ── Local emulator mode (development only) ──────────────────────────────────
// Set REACT_APP_USE_EMULATORS=true to point the panel at the local Firebase
// emulators instead of a real project, so the UI can be exercised end-to-end
// against disposable data. See qa/ui-harness/.
//
// Guarded twice: REACT_APP_USE_EMULATORS must be set AND the build must not be
// a production build. NODE_ENV is substituted at build time, so in a
// production bundle this condition is constant-false and the branch can never
// execute, whatever REACT_APP_USE_EMULATORS is set to at runtime.
//
// Note this is a *reachability* guarantee, not a code-stripping one: the
// statements may still appear in the bundle. Nothing secret lives here, but do
// not put anything behind this flag that would be harmful merely by being
// present in a deployed bundle.
export const usingEmulators =
  process.env.NODE_ENV !== 'production' && process.env.REACT_APP_USE_EMULATORS === 'true';

if (process.env.NODE_ENV !== 'production' && process.env.REACT_APP_USE_EMULATORS === 'true') {
  const host = process.env.REACT_APP_EMULATOR_HOST || '127.0.0.1';
  connectAuthEmulator(auth, `http://${host}:9099`, { disableWarnings: true });
  connectFirestoreEmulator(db, host, 8080);
  connectFunctionsEmulator(functions, host, 5001);
  console.warn(`🧪 Firebase EMULATOR mode — Auth :9099, Firestore :8080, Functions :5001 on ${host}`);
}

// Initialize Firebase Messaging (only if supported in browser)
let messagingInstance: ReturnType<typeof getMessaging> | null = null;

const initializeMessaging = async () => {
  try {
    const supported = await isSupported();
    if (supported) {
      messagingInstance = getMessaging(app);
      console.log('✅ Firebase Messaging initialized');
    } else {
      console.warn('⚠️ Firebase Messaging is not supported in this browser');
    }
  } catch (error) {
    console.error('❌ Error initializing Firebase Messaging:', error);
  }
};

// Initialize messaging
initializeMessaging();

export const messaging = messagingInstance;

export default app;